import React, { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { Spinner } from '@okcredit/web-lib'
import { THEME } from 'components/theme'
import { broadcastEvent } from 'state/dashboard/dashboard.actions'
import AppImage from 'components/AppImage'
import { ZoomableImage } from 'components/ZoomableImage'
import { resolveImageUrl } from 'utils/imageUrl'
import { AnchoredSheet, findSheetAnchor } from 'components/TransactionAlertSettings/AnchoredSheet'
import { getUserInitials } from 'utils/getUserInitials'
import { ProfilePhotoTarget, useProfilePhotoUpload } from './useProfilePhotoUpload'
import { ProfilePhotoAvatarStyles } from './ProfilePhotoAvatar.styles'

// Material glyphs: edit (pencil), delete, image
const PencilGlyph: FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
)
// iOS UploadImageScreen glyphs: outline bin ("delete_outline") and outline photo library
const TrashGlyph: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5zM18 7H6v12a2.006 2.006 0 0 0 2 2h8a2.006 2.006 0 0 0 2-2z" />
  </svg>
)
const ImageGlyph: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 4v12H8V4h12m0-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 9.67 1.69 2.26 2.48-3.1L19 15H9zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" />
  </svg>
)

const BackGlyph: FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
)

export interface ProfilePhotoAvatarProps {
  target: ProfilePhotoTarget
  // Saved photo URL (Drive) from the store
  imageUrl?: string
  // For the initials fallback
  name?: string
  // Avatar diameter; the pencil badge scales with it
  size?: number
  // Show the pencil badge (profile screens); the ledger header keeps a plain avatar
  showEditBadge?: boolean
  // Rendered inside the avatar on top of the image (e.g. registered / premium tag)
  children?: React.ReactNode
  className?: string
  // Image fallback colour when there is no photo (defaults to the pale green disc)
  style?: React.CSSProperties
  // Where this avatar lives (analytics): profile_drawer | business_switcher | ledger_header
  source?: string
  // Replaces the default tap behaviour (viewer / photo sheet), e.g. the ledger header avatar
  // just opens the account's profile
  onAvatarClick?: () => void
}

// The iOS/Android profile avatar. Tapping the photo opens it large — view only, with a close
// button — inside the drawer / panel the avatar sits in, or centred on the page where there
// is no such panel (ledger header). Changing the photo is the pencil's job: tapping the pencil badge — or an avatar
// with no photo yet — opens the "Delete / Choose photo" sheet anchored to the drawer it sits
// in → Drive-gated upload (or removal) → saved on the account / business.
// The viewer shows the photo at up to 520px; asking Drive for the 1024px bucket (2x of 512)
// is sharp enough and a fraction of the original file
const VIEWER_MAX_WIDTH = 512

export const ProfilePhotoAvatar: FC<ProfilePhotoAvatarProps> = ({
  target,
  imageUrl,
  name = '',
  size = 88,
  showEditBadge = true,
  children,
  className,
  style,
  source = 'profile_drawer',
  onAvatarClick: onAvatarClickOverride
}) => {
  const classes = ProfilePhotoAvatarStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const targetName =
    target.kind === 'business'
      ? 'business'
      : target.entityType === 'customer'
      ? 'customer'
      : 'supplier'
  const track = (event: string, props: Record<string, unknown> = {}) =>
    dispatch(broadcastEvent(event, { target: targetName, source, ...props }))
  const photo = useProfilePhotoUpload(target, imageUrl)
  const rootRef = useRef<HTMLButtonElement>(null)
  const badgeRef = useRef<HTMLSpanElement>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  // The large photo has arrived; until then the viewer shows the avatar's cached thumbnail
  // blurred up with a spinner, instead of an empty card
  const [viewerLoaded, setViewerLoaded] = useState(false)
  const [sheetAnchor, setSheetAnchor] = useState<HTMLElement | null>(null)
  const [imageError, setImageError] = useState(false)
  useEffect(() => {
    setImageError(false)
  }, [imageUrl])
  useEffect(() => {
    setSheetAnchor(findSheetAnchor(rootRef.current))
  }, [])

  // Escape closes the viewer only (capture phase, so the drawer underneath stays open)
  useEffect(() => {
    if (!viewerOpen) {
      return undefined
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setViewerOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [viewerOpen])

  // The enlarged photo covers the panel it was opened from (same box the sheet docks to);
  // measured when it opens and again if the window is resized
  const [viewerRect, setViewerRect] = useState<DOMRect | null>(null)
  useEffect(() => {
    if (!viewerOpen || !sheetAnchor) {
      setViewerRect(null)
      return undefined
    }
    const measure = () => setViewerRect(sheetAnchor.getBoundingClientRect())
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [viewerOpen, sheetAnchor])

  const shown = photo.previewUrl || imageUrl
  const hasPhoto = Boolean(shown) && (!imageError || shown === photo.previewUrl)
  const viewerStageRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!viewerOpen || viewerLoaded) {
      return
    }
    const img = viewerStageRef.current?.querySelector<HTMLImageElement>('img[data-viewer-photo]')
    if (img && img.complete && img.naturalWidth > 0) {
      setViewerLoaded(true)
    }
  }, [viewerOpen, viewerLoaded, shown])
  // Delete is offered for the business photo only: the identity API clears it (null +
  // update_profile_image), while the ledger API ignores a blank/null profile_image for
  // customers and suppliers (verified on staging), so there removal would silently no-op.
  const canDelete = Boolean(imageUrl) && target.kind === 'business'
  const badgeSize = Math.max(22, Math.round(size * 0.34))

  const openSheet = () => {
    track('profile_photo_sheet_viewed', { has_photo: Boolean(imageUrl) })
    setSheetOpen(true)
  }
  // Drive takes ~1s per image whatever its size, so start fetching the viewer-size photo
  // when the pointer reaches the avatar: by the tap it is usually in the browser cache and
  // the viewer opens sharp instead of on the spinner. Once per URL.
  const prefetchedRef = useRef<string | null>(null)
  const prefetchViewerImage = () => {
    if (onAvatarClickOverride || !hasPhoto || !shown || prefetchedRef.current === shown) {
      return
    }
    prefetchedRef.current = shown
    const img = new Image()
    img.src = resolveImageUrl(shown, { width: VIEWER_MAX_WIDTH })
  }
  const onAvatarClick = (e: React.MouseEvent) => {
    if (onAvatarClickOverride) {
      onAvatarClickOverride()
      return
    }
    const onBadge = Boolean(badgeRef.current && badgeRef.current.contains(e.target as Node))
    if (hasPhoto && !onBadge) {
      track('profile_photo_viewed')
      setViewerLoaded(false)
      setViewerOpen(true)
    } else {
      openSheet()
    }
  }
  const onChoose = () => {
    track('profile_photo_action_clicked', { action: 'choose' })
    setSheetOpen(false)
    setViewerOpen(false)
    photo.openPicker()
  }
  const onDelete = () => {
    track('profile_photo_action_clicked', { action: 'delete' })
    setSheetOpen(false)
    setViewerOpen(false)
    photo.removePhoto()
  }

  const actions = (
    <div className={classes.actions}>
      {canDelete ? (
        <button type="button" className={classes.action} onClick={onDelete}>
          <span className={classNames(classes.actionDisc, classes.actionDiscDanger)}>
            <TrashGlyph />
          </span>
          {t('dashboard:DeletePhoto')}
        </button>
      ) : null}
      <button type="button" className={classes.action} onClick={onChoose}>
        <span className={classNames(classes.actionDisc, classes.actionDiscPrimary)}>
          <ImageGlyph />
        </span>
        {t('dashboard:ChoosePhoto')}
      </button>
    </div>
  )

  return (
    <>
      <button
        ref={rootRef}
        type="button"
        className={classNames(classes.button, { [classes.ringed]: showEditBadge }, className)}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.32), ...style }}
        onClick={onAvatarClick}
        onMouseEnter={prefetchViewerImage}
        onFocus={prefetchViewerImage}
        disabled={photo.uploading}
        title={onAvatarClickOverride ? undefined : t('dashboard:ChangePhoto')}
        aria-label={onAvatarClickOverride ? name : t('dashboard:ChangePhoto')}
      >
        {hasPhoto ? (
          <AppImage
            src={shown}
            displayWidth={size}
            width={size}
            height={size}
            loading="eager"
            className={classes.image}
            alt=""
            onError={() => setImageError(true)}
          />
        ) : (
          <span>{getUserInitials(name).toUpperCase()}</span>
        )}
        {!photo.uploading ? children : null}
        {photo.uploading ? (
          <span className={classes.uploadingOverlay} aria-live="polite">
            <span
              className={classes.uploadingSpinner}
              style={{ width: Math.round(size * 0.3), height: Math.round(size * 0.3) }}
            />
          </span>
        ) : showEditBadge ? (
          <span
            ref={badgeRef}
            className={classes.editBadge}
            style={{ width: badgeSize, height: badgeSize }}
          >
            <PencilGlyph size={Math.round(badgeSize * 0.55)} />
          </span>
        ) : null}
      </button>
      {photo.input}
      {photo.dialog}
      {sheetOpen ? (
        <AnchoredSheet
          anchor={sheetAnchor}
          ariaLabel={t('dashboard:ChangePhoto')}
          sheetClassName={classes.compactSheet}
          onDismiss={() => setSheetOpen(false)}
        >
          {/* iOS UploadImageScreen: just the round actions, no title bar — scrim tap closes */}
          {actions}
        </AnchoredSheet>
      ) : null}
      {viewerOpen && hasPhoto && typeof document !== 'undefined'
        ? createPortal(
            <div
              className={classNames(classes.viewerScrim, {
                [classes.viewerScrimAnchored]: Boolean(viewerRect)
              })}
              style={
                viewerRect
                  ? {
                      top: viewerRect.top,
                      left: viewerRect.left,
                      width: viewerRect.width,
                      height: viewerRect.height
                    }
                  : undefined
              }
              onClick={() => setViewerOpen(false)}
            >
              {/* role="dialog" keeps the drawer underneath open; data-theme-local pins the
                  card's literal black backdrop so the dark theme does not lift it to grey */}
              <div
                className={classNames(classes.viewerCard, {
                  [classes.viewerCardAnchored]: Boolean(viewerRect)
                })}
                role="dialog"
                aria-modal="true"
                aria-label={name}
                data-theme-local="light"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className={classes.viewerClose}
                  onClick={() => setViewerOpen(false)}
                  aria-label={t('dashboard:Close')}
                  title={t('dashboard:Close')}
                >
                  <BackGlyph />
                </button>
                <div
                  ref={viewerStageRef}
                  className={classNames(classes.viewerStage, {
                    [classes.viewerStageAnchored]: Boolean(viewerRect)
                  })}
                >
                  {!viewerLoaded ? (
                    <>
                      {/* Same proxied URL the avatar already loaded, so it paints at once */}
                      <AppImage
                        src={shown}
                        displayWidth={size}
                        loading="eager"
                        className={classes.viewerPlaceholder}
                        alt=""
                        aria-hidden="true"
                      />
                      <span className={classes.viewerSpinner} aria-live="polite">
                        <Spinner size={40} color={THEME.brandGreen} />
                      </span>
                    </>
                  ) : null}
                  {/* Click / double-click to toggle, scroll or pinch to zoom, drag to pan —
                      the same viewport the bills viewer uses */}
                  <ZoomableImage
                    src={shown}
                    className={classNames(classes.viewerZoom, {
                      [classes.viewerZoomAnchored]: Boolean(viewerRect)
                    })}
                  >
                    {/* Sized for the viewer (2x of its max width), not the multi-MB original */}
                    <AppImage
                      src={shown}
                      displayWidth={VIEWER_MAX_WIDTH}
                      loading="eager"
                      className={classNames(classes.viewerImage, {
                        [classes.viewerImageAnchored]: Boolean(viewerRect),
                        [classes.viewerImagePending]: !viewerLoaded
                      })}
                      alt={name}
                      draggable={false}
                      data-viewer-photo=""
                      onLoad={() => setViewerLoaded(true)}
                      onError={() => setViewerOpen(false)}
                    />
                  </ZoomableImage>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  )
}

export default ProfilePhotoAvatar
